import { useCallback, useEffect, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { nok } from "@/pages/admin/budsjett/budsjettUtils";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

const Tall = ({ label, verdi, onLagre, testid }) => (
  <label className="adm-label">
    {label}
    <input
      type="number"
      defaultValue={verdi ?? 0}
      onBlur={(e) => Number(e.target.value) !== verdi && onLagre(Number(e.target.value) || 0)}
      className="adm-input"
      data-testid={testid}
    />
  </label>
);

const Flagg = ({ label, verdi, onLagre, testid }) => (
  <button
    type="button"
    onClick={() => onLagre(!verdi)}
    className={`inline-flex items-center gap-2 text-[11.5px] font-medium rounded-lg px-2.5 py-1.5 border transition-colors ${
      verdi ? "bg-neutral-900 text-white border-neutral-900" : "text-neutral-500 border-[#DED9CD] hover:text-neutral-800"
    }`}
    data-testid={testid}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${verdi ? "bg-[#F1E75A]" : "bg-neutral-300"}`} />
    {label}
  </button>
);

const Tekst = ({ label, verdi, onLagre, testid }) => {
  const [v, setV] = useState(verdi ?? "");
  useEffect(() => { setV(verdi ?? ""); }, [verdi]);
  return (
    <label className="adm-label">
      {label}
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== (verdi ?? "") && onLagre(v)}
        className="adm-input"
        data-testid={testid}
      />
    </label>
  );
};

export default function ProductsModal({ onLukk }) {
  const [products, setProducts] = useState(null);
  const [utvidet, setUtvidet] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/os/partnership-products");
      setProducts(data.products);
    } catch (e) {
      feil(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onLukk(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onLukk]);

  const patch = async (pid, p) => {
    try {
      const { data } = await api.patch(`/admin/os/partnership-products/${pid}`, p);
      setProducts((prev) => prev.map((x) => (x.id === pid ? data.item : x)));
    } catch (e) {
      feil(e);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4" data-testid="products-modal">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onLukk} />
      <div className="sak-modal relative w-full max-w-[620px] max-h-[82vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-[#E7E4DB] p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-[15px] font-semibold">Partnership-produkter</h2>
          <button type="button" onClick={onLukk} className="text-neutral-400 hover:text-neutral-800 transition-colors" data-testid="products-modal-lukk">
            <X size={16} />
          </button>
        </div>
        <p className="text-[11.5px] text-neutral-400 mb-4">
          Globale seed-priser og standardleveranser — proposals kan overstyre per selskap.
          Hovedscenen er redaksjonelt kuratert og aldri en del av pakkene.
        </p>

        {products == null ? (
          <p className="text-[12px] text-neutral-400 py-8 text-center" data-testid="products-laster">Laster …</p>
        ) : (
          products.map((p) => {
            const d = p.deliverables || {};
            const setD = (k) => (v) => patch(p.id, { deliverables: { [k]: v } });
            const open = utvidet === p.id;
            return (
              <div key={p.id} className="border-t border-[#EEEBE2] first:border-t-0" data-testid={`product-${p.key}`}>
                <button
                  type="button"
                  onClick={() => setUtvidet(open ? null : p.id)}
                  className="w-full flex items-baseline gap-3 py-3 text-left"
                  data-testid={`product-toggle-${p.key}`}
                >
                  <span className="text-[13.5px] font-medium text-neutral-900">{p.name}</span>
                  <span className="text-[12px] tabular-nums text-neutral-500">{nok(p.price)} eks. mva</span>
                  <span className="text-[11px] text-neutral-400 flex-1 truncate">{p.blurb}</span>
                  <ChevronDown size={13} className={`text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="pb-4" data-testid={`product-detaljer-${p.key}`}>
                    <div className="grid grid-cols-3 gap-3">
                      <Tall label="Pris eks. mva" verdi={p.price} onLagre={(v) => patch(p.id, { price: v })} testid={`product-pris-${p.key}`} />
                      <Tall label="Billetter" verdi={d.physicalTickets} onLagre={setD("physicalTickets")} testid={`product-tickets-${p.key}`} />
                      <Tall label="Middagsplasser" verdi={d.dinnerSeats} onLagre={setD("dinnerSeats")} testid={`product-dinner-${p.key}`} />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Flagg label="Expo-aktivering" verdi={!!d.expoActivation} onLagre={setD("expoActivation")} testid={`product-expo-${p.key}`} />
                      <Flagg label="Kategorieksklusivitet" verdi={!!d.categoryExclusivity} onLagre={setD("categoryExclusivity")} testid={`product-exclusivity-${p.key}`} />
                      <Flagg label="Executive roundtable" verdi={!!d.executiveRoundtable} onLagre={setD("executiveRoundtable")} testid={`product-roundtable-${p.key}`} />
                      <label className="adm-label" style={{ minWidth: 140 }}>
                        <select
                          value={d.broadcastVisibility || "standard"}
                          onChange={(e) => setD("broadcastVisibility")(e.target.value)}
                          className="adm-input"
                          data-testid={`product-broadcast-${p.key}`}
                        >
                          <option value="none">Broadcast: ingen</option>
                          <option value="standard">Broadcast: standard</option>
                          <option value="premium">Broadcast: premium</option>
                        </select>
                      </label>
                    </div>
                    <div className="grid grid-cols-1 gap-3 mt-3">
                      <Tekst label="Brand presence" verdi={d.brandPresence} onLagre={setD("brandPresence")} testid={`product-brand-${p.key}`} />
                      <Tekst label="Hospitality" verdi={d.hospitality} onLagre={setD("hospitality")} testid={`product-hospitality-${p.key}`} />
                      <Tekst label="Content rights" verdi={d.contentRights} onLagre={setD("contentRights")} testid={`product-content-${p.key}`} />
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
